/**
 * Credit Card Payoff Calculator Page
 * Calculate how long it will take to pay off your credit card debt
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface CreditCardPayoffData {
  current_balance: number;
  monthly_payment: number;
  interest_rate: number;
  extra_payment?: number;
}

interface CreditCardPayoffResults {
  months_to_payoff: number;
  years_to_payoff: number;
  total_interest: number;
  total_paid: number;
  months_saved?: number;
  interest_saved?: number;
}

export default function CreditCardPayoffPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<CreditCardPayoffData>({
    current_balance: 0,
    monthly_payment: 0,
    interest_rate: 18,
    extra_payment: 0,
  });
  const [results, setResults] = useState<CreditCardPayoffResults | null>(null);
  const [resultsWithExtra, setResultsWithExtra] = useState<CreditCardPayoffResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/credit-card-payoff', 'Credit Card Payoff Calculator');
    document.title = `${t('calculators.creditCardPayoff.title')} | PayWise365`;
  }, [t]);

  const calculatePayoff = (balance: number, monthlyPayment: number, rate: number) => {
    const monthlyRate = rate / 100 / 12;
    let currentBalance = balance;
    let months = 0;
    let totalInterest = 0;
    const maxMonths = 600;

    while (currentBalance > 0.01 && months < maxMonths) {
      const interest = currentBalance * monthlyRate;
      totalInterest += interest;
      currentBalance = currentBalance + interest - monthlyPayment;
      if (currentBalance < 0) currentBalance = 0;
      months++;
    }

    return {
      months_to_payoff: months,
      years_to_payoff: months / 12,
      total_interest: totalInterest,
      total_paid: balance + totalInterest,
    };
  };

  const handleCalculate = () => {
    const normalResults = calculatePayoff(
      formData.current_balance,
      formData.monthly_payment,
      formData.interest_rate
    );
    setResults(normalResults);

    if (formData.extra_payment && formData.extra_payment > 0) {
      const extraResults = calculatePayoff(
        formData.current_balance,
        formData.monthly_payment + formData.extra_payment,
        formData.interest_rate
      );
      setResultsWithExtra(extraResults);
    } else {
      setResultsWithExtra(null);
    }
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.creditCardPayoff.title')}</h1>
          <p>{t('calculators.creditCardPayoff.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Current Balance (HKD)"
                    type="number"
                    value={formData.current_balance || ''}
                    onChange={(e) => setFormData({ ...formData, current_balance: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Monthly Payment (HKD)"
                    type="number"
                    value={formData.monthly_payment || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_payment: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Annual Interest Rate (%)"
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) || 0 })}
                    helperText="Typical credit card rate: 15-25%"
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Extra Monthly Payment (Optional)"
                    type="number"
                    value={formData.extra_payment || ''}
                    onChange={(e) => setFormData({ ...formData, extra_payment: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <>
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Current Payment Plan
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                    <Box>
                      <Box sx={{ p: 2, bgcolor: 'error.main', borderRadius: 2, color: 'white' }}>
                        <Typography variant="body2">Time to Pay Off</Typography>
                        <Typography variant="h5">{results.years_to_payoff.toFixed(1)} years</Typography>
                        <Typography variant="body2">({results.months_to_payoff} months)</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ p: 2, bgcolor: 'error.dark', borderRadius: 2, color: 'white' }}>
                        <Typography variant="body2">Total Interest</Typography>
                        <Typography variant="h5">HKD {results.total_interest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                        <Typography variant="body2">Total Amount Paid</Typography>
                        <Typography variant="h5">HKD {results.total_paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {resultsWithExtra && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                      With Extra Payment
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                      <Box>
                        <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'white' }}>
                          <Typography variant="body2">Time to Pay Off</Typography>
                          <Typography variant="h5">{resultsWithExtra.years_to_payoff.toFixed(1)} years</Typography>
                          <Typography variant="body2">({resultsWithExtra.months_to_payoff} months)</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={{ p: 2, bgcolor: 'success.dark', borderRadius: 2, color: 'white' }}>
                          <Typography variant="body2">Interest Saved</Typography>
                          <Typography variant="h5">HKD {(results.total_interest - resultsWithExtra.total_interest).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, color: 'white' }}>
                          <Typography variant="body2">Months Saved</Typography>
                          <Typography variant="h5">{results.months_to_payoff - resultsWithExtra.months_to_payoff} months</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
